import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { BookOpen, Plus, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const turnoLabels = {
    manha: 'Manhã',
    tarde: 'Tarde',
    noite: 'Noite'
};

const LPTEPage = () => {
    const [lptes, setLptes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { hasRole, user } = useAuth();

    const [formData, setFormData] = useState({
        turno_saida: 'manha',
        turno_entrada: 'tarde',
        operador_saida_nome: '',
        pendencias: '',
        ocorrencias_relevantes: '',
        status_comboios: '',
        observacoes: ''
    });

    useEffect(() => {
        fetchLptes();
    }, []);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({ ...prev, operador_saida_nome: user.nome }));
        }
    }, [user]);

    const fetchLptes = async () => {
        try {
            const response = await axios.get(`${API}/lptes`);
            setLptes(response.data);
        } catch (error) {
            toast.error('Erro ao carregar registros');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            await axios.post(`${API}/lptes`, formData);
            toast.success('Passagem de turno registrada!');
            setDialogOpen(false);
            resetForm();
            fetchLptes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar');
        }
    };

    const handleAceite = async (lpteId) => {
        try {
            await axios.post(`${API}/lptes/${lpteId}/aceite`);
            toast.success('Turno aceito com sucesso!');
            fetchLptes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao aceitar turno');
        }
    };

    const resetForm = () => {
        setFormData({
            turno_saida: 'manha',
            turno_entrada: 'tarde',
            operador_saida_nome: user?.nome || '',
            pendencias: '',
            ocorrencias_relevantes: '',
            status_comboios: '',
            observacoes: ''
        });
    };

    const canCreate = hasRole(['admin', 'gestor', 'operador']);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="lpte-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Livro de Passagem de Turno
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Registro estruturado de passagem entre turnos
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-lpte"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Registrar Passagem
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Passagem de Turno
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Turno de Saída
                                        </Label>
                                        <Select 
                                            value={formData.turno_saida} 
                                            onValueChange={(value) => setFormData({...formData, turno_saida: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(turnoLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Turno de Entrada
                                        </Label>
                                        <Select 
                                            value={formData.turno_entrada} 
                                            onValueChange={(value) => setFormData({...formData, turno_entrada: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(turnoLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Pendências *
                                    </Label>
                                    <Textarea
                                        data-testid="input-lpte-pendencias"
                                        value={formData.pendencias}
                                        onChange={(e) => setFormData({...formData, pendencias: e.target.value})}
                                        required
                                        placeholder="Descreva as pendências a serem passadas..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[100px]"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Ocorrências Relevantes
                                    </Label>
                                    <Textarea
                                        value={formData.ocorrencias_relevantes}
                                        onChange={(e) => setFormData({...formData, ocorrencias_relevantes: e.target.value})}
                                        placeholder="ROOs, sinistros ou eventos importantes..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Status dos Comboios
                                    </Label>
                                    <Textarea
                                        value={formData.status_comboios}
                                        onChange={(e) => setFormData({...formData, status_comboios: e.target.value})}
                                        placeholder="Situação atual da frota..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-lpte"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Registrar
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* LPTE List */}
            {lptes.length === 0 ? (
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-12 text-center">
                        <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">Nenhuma passagem de turno registrada</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {lptes.map((lpte) => (
                        <Card 
                            key={lpte.id} 
                            data-testid={`lpte-card-${lpte.id}`}
                            className="bg-slate-900/50 border-slate-800 rounded-sm"
                        >
                            <CardHeader className="border-b border-slate-800 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                                            {turnoLabels[lpte.turno_saida]}
                                            <ArrowRight className="w-4 h-4 text-cyan-400" />
                                            {turnoLabels[lpte.turno_entrada]}
                                        </CardTitle>
                                        {lpte.aceite_entrada ? (
                                            <span className="badge-success px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm flex items-center gap-1">
                                                <CheckCircle className="w-3 h-3" />
                                                Aceito
                                            </span>
                                        ) : (
                                            <span className="badge-warning px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                Aguardando Aceite
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 font-mono">
                                        {formatDate(lpte.criado_em)}
                                    </p>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Operador Saída</p>
                                        <p className="text-white mt-1">{lpte.operador_saida_nome}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Operador Entrada</p>
                                        <p className="text-white mt-1">{lpte.operador_entrada_nome || '-'}</p>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Pendências</p>
                                    <p className="text-slate-300 mt-1 text-sm">{lpte.pendencias}</p>
                                </div>

                                {lpte.ocorrencias_relevantes && (
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Ocorrências Relevantes</p>
                                        <p className="text-slate-300 mt-1 text-sm">{lpte.ocorrencias_relevantes}</p>
                                    </div>
                                )}

                                {lpte.status_comboios && (
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Status dos Comboios</p>
                                        <p className="text-slate-300 mt-1 text-sm">{lpte.status_comboios}</p>
                                    </div>
                                )}

                                {!lpte.aceite_entrada && canCreate && (
                                    <div className="border-t border-slate-800 pt-4">
                                        <Button
                                            onClick={() => handleAceite(lpte.id)}
                                            data-testid={`btn-aceite-${lpte.id}`}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                                        >
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                            Aceitar Turno
                                        </Button>
                                    </div>
                                )}

                                {lpte.aceite_entrada && (
                                    <p className="text-xs text-emerald-400 border-t border-slate-800 pt-3">
                                        Aceito em: {formatDate(lpte.aceite_em)}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LPTEPage;
